import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

interface PersonStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  person: {
    name: string;
    nameEn: string;
    tagline: string;
    taglineEn: string;
    imageUrl: string;
    story: string;
    storyEn: string;
    detailedStory: string;
    detailedStoryEn: string;
    background: string;
    backgroundEn: string;
    challenge: string;
    challengeEn: string;
    future: string;
    futureEn: string;
  };
  language: string;
}

const PersonStoryModal = ({ isOpen, onClose, person, language }: PersonStoryModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleBackdropClick}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 overflow-hidden relative"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all hover:scale-110"
              aria-label="Close"
            >
              <i className="ri-close-line text-2xl text-gray-700"></i>
            </button>

            {/* Header with Image */}
            <div className="relative h-96 overflow-hidden">
              <img
                src={person.imageUrl}
                alt={language === 'ja' ? person.name : person.nameEn}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 20%' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h2 className="text-4xl font-black mb-2">
                  {language === 'ja' ? person.name : person.nameEn}
                </h2>
                <p className="text-xl font-medium text-amber-300">
                  {language === 'ja' ? person.tagline : person.taglineEn}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 max-h-[60vh] overflow-y-auto">
              {/* Introduction */}
              <div className="mb-8">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {language === 'ja' ? person.detailedStory : person.detailedStoryEn}
                </p>
              </div>

              {/* Background */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg">
                    <i className="ri-user-line text-xl text-amber-700"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {language === 'ja' ? 'バックグラウンド' : 'Background'}
                  </h3>
                </div>
                <p className="text-base text-gray-700 leading-relaxed pl-13">
                  {language === 'ja' ? person.background : person.backgroundEn}
                </p>
              </div>

              {/* Challenge */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                    <i className="ri-lightbulb-line text-xl text-orange-700"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {language === 'ja' ? '今の挑戦' : 'Current Challenge'}
                  </h3>
                </div>
                <p className="text-base text-gray-700 leading-relaxed pl-13">
                  {language === 'ja' ? person.challenge : person.challengeEn}
                </p>
              </div>

              {/* Future */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                    <i className="ri-rocket-line text-xl text-green-700"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {language === 'ja' ? 'これから' : 'Future Vision'}
                  </h3>
                </div>
                <p className="text-base text-gray-700 leading-relaxed pl-13">
                  {language === 'ja' ? person.future : person.futureEn}
                </p>
              </div>

              {/* CTA */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <div className="bg-stone-50 rounded-xl p-6 text-center">
                  <p className="text-base text-gray-700 mb-4">
                    {language === 'ja' 
                      ? 'この人に直接相談してみませんか？' 
                      : 'Would you like to talk to this person directly?'}
                  </p>
                  <button
                    onClick={() => {
                      onClose();
                      document.getElementById('gateway')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg font-bold transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                    {language === 'ja' ? '相談窓口へ' : 'Go to Consultation'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PersonStoryModal;
